import * as React from 'react';

import classnames from 'classnames';

import {OverlappingAvatars} from 'shells/overlapping-avatars/overlapping-avatars';
import {TimelineEntryStatusBar} from 'shells/timeline/entry/content/timeline-entry-status-bar';
import {TruncatedTimelineEntryContent} from 'shells/timeline/entry/content/truncated-timeline-entry-content';
import type {Participant, Lead, TimelineEntryStatus} from 'shells/timeline/types';

import {analyticsClient} from '../../../analytics-client';

import {SuggestedReplyWrapper} from '../../email/ai-replies';

import {useTimelineEmail} from './timeline-email-context';
import {TimelineEntryExpandedContentWrapper} from './timeline-entry-expanded-content-wrapper';

import styles from './timeline-entry-content.module.css';

// TimelineEntryContentProps
export type Props = {
    subject?: string | React.ReactNode;
    subjectTitle?: string;
    // This will be passed in if used in a modal, not if in the main timeline
    htmlContent?: string;
    // Content passed in this prop is in a displayable format
    content?: string | React.ReactNode | null | undefined;
    // Content that is truncated in a custom way
    truncatedContent?: React.ReactNode;
    // Content that needs to be placed in a rich-textarea to be displayed properly
    status?: TimelineEntryStatus;
    participants?: Participant[] | null | undefined;
    relatedLeads?: Lead[];
    metadata?: React.ReactNode;
    isExpanded?: boolean;
    maxHeight?: number;
    externalMeetingTranscriptSummary?: React.ReactNode;
    isDashboard?: boolean;
};

type OwnProps = Props & {
    changeLogEntryId: string;
    onClickOpen?: (e: React.SyntheticEvent<any>) => void;
    actions?: React.ReactNode;
};

export function TimelineEntryContent(props: OwnProps) {
    const {
        changeLogEntryId,
        subject,
        subjectTitle,
        content,
        truncatedContent,
        status,
        participants,
        relatedLeads,
        metadata,
        onClickOpen,
        actions,
        isExpanded,
        htmlContent,
        maxHeight,
        externalMeetingTranscriptSummary,
        isDashboard,
    } = props;

    const ref = React.useRef();
    const [width, setWidth] = React.useState<number>(0);

    const emailTimelineContext = useTimelineEmail();

    React.useLayoutEffect(() => {
        if (ref.current) {
            // @ts-expect-error
            setWidth(ref.current.clientWidth + 20);
        } else {
            setWidth(0);
        }
    }, [participants, actions, metadata]);

    const shouldCenterAlign = Boolean((subject && !status) || (!subject && status));
    const shouldDisplayRowReverse = !subject && !status && (participants || actions);

    return (
        <div
            onClick={onClickOpen}
            className={classnames({
                [styles['timeline-entry-content']]: true,
                [styles['timeline-entry-content--row-view']]: shouldDisplayRowReverse,
                [styles['timeline-entry-content--expandable']]: onClickOpen,
            })}
        >
            {status || subject || participants || actions || metadata ? (
                <div
                    className={classnames(
                        shouldCenterAlign ? 'align-center' : undefined,
                        styles['content-heading-container']
                    )}
                >
                    {status || subject ? (
                        <div style={{width: `calc(100% - ${width}px)`}}>
                            {status ? (
                                <div className={styles['status-bar-container']}>
                                    <TimelineEntryStatusBar status={status} />
                                </div>
                            ) : undefined}
                            {subject ? (
                                <div
                                    // @ts-expect-error
                                    title={subjectTitle || subject}
                                    className={classnames(
                                        onClickOpen ? 'truncate' : undefined,
                                        styles.subject
                                    )}
                                >
                                    {subject}
                                </div>
                            ) : undefined}
                        </div>
                    ) : undefined}
                    {participants || actions || metadata ? (
                        // @ts-expect-error
                        <div ref={ref} className={styles['participant-actions-container']}>
                            {participants ? (
                                <OverlappingAvatars
                                    avatars={participants}
                                    relatedLeads={relatedLeads}
                                    size={28}
                                    maxDisplayed={4}
                                    withTooltip={true}
                                />
                            ) : undefined}
                            {actions}
                            {metadata}
                        </div>
                    ) : undefined}
                </div>
            ) : undefined}
            {onClickOpen && !isExpanded ? (
                truncatedContent || <TruncatedTimelineEntryContent content={content} />
            ) : (
                <TimelineEntryExpandedContentWrapper
                    changeLogEntryId={changeLogEntryId}
                    content={content}
                    maxHeight={maxHeight}
                    htmlContent={htmlContent}
                />
            )}
            {externalMeetingTranscriptSummary}
            {NutClientConfig.enableAiEmailReplies &&
                NutClientConfig.hasAiEmailReplies &&
                emailTimelineContext &&
                emailTimelineContext.allowSuggestedReplies &&
                !isDashboard && (
                    <div className='pad-8'>
                        <SuggestedReplyWrapper
                            suggestedReplies={emailTimelineContext.suggestedEmailReplies}
                            isLoading={emailTimelineContext.isLoadingSuggestedReplies}
                            onCardClicked={(reply) => {
                                emailTimelineContext.onReply(reply.id);
                                analyticsClient.track(
                                    analyticsClient.EVENTS.TRACK_AI_EMAIL_REPLY_STAGED_FROM_TIMELINE
                                );
                            }}
                            variant='sparse'
                        />
                    </div>
                )}
        </div>
    );
}
